import { useEffect, useState } from 'react';
import { useInitialRender } from '@goparrot-dashboard/shared-utils/src/hooks/useInitialRender';
import { SSO_CALLBACK_ERROR, SSO_CALLBACK_FLAG } from '@goparrot-dashboard/shared-utils';
import { useSsoConnectCallback } from '../../../../api/auth';
import { getSsoUrlParams } from '../../../../api/auth/src/utils/getSsoUrlParams';
import { ssoConnectCallbackSuccess } from '../../../auth/src/components/helpers/ssoConnectCallbackSuccess';

const SSO_TIMEOUT_MS = 60 * 1000; // 1 minute

export const enum SsoLinkState {
  'NONE',
  'SUCCESS',
  'FAILURE',
}

export const enum SsoErrorType {
  'NONE',
  'CONFLICT',
  'GENERAL',
}

export const useSsoConnectCallbackMutation = ({ hideSsoLinkBanner = undefined }: { hideSsoLinkBanner?: () => void } = {}): {
  ssoLinkState: SsoLinkState;
  ssoErrorType: SsoErrorType;
} => {
  const ssoUrlParams = getSsoUrlParams();
  const [ssoLinkState, setSsoLinkState] = useState(SsoLinkState.NONE);
  const [ssoErrorType, setSsoErrorType] = useState(SsoErrorType.NONE);
  const { isFirstRender: isInitialRender } = useInitialRender();

  const ssoConnectCallback = useSsoConnectCallback({
    onSuccess: () => {
      ssoConnectCallbackSuccess({ hideSsoLinkBanner, ssoLinkState, setSsoLinkState, clearParams: ssoUrlParams.clearParams });
      setSsoErrorType(SsoErrorType.NONE);
      localStorage.removeItem(SSO_CALLBACK_ERROR);
      // Don't remove the flag here - we need to wait for the user data to be updated
    },
    onError: (err) => {
      setSsoLinkState(SsoLinkState.FAILURE);
      setSsoErrorType(err?.response?.status === 409 ? SsoErrorType.CONFLICT : SsoErrorType.GENERAL);
      // Clear the flag on error since we won't be getting updated user data
      localStorage.removeItem(SSO_CALLBACK_FLAG);
      // set that there was an error\
      localStorage.setItem(SSO_CALLBACK_ERROR, 'true');
    },
  });

  useEffect(() => {
    if (isInitialRender && ssoUrlParams && ssoUrlParams.code && ssoUrlParams.state) {
      const isCallbackInProgress = localStorage.getItem(SSO_CALLBACK_FLAG);
      if (!isCallbackInProgress) {
        // Set the flag before making the call
        localStorage.setItem(SSO_CALLBACK_FLAG, 'true');
        // Set a timeout to clear the flag after 5 minutes
        const timeout = setTimeout(() => {
          localStorage.removeItem(SSO_CALLBACK_FLAG);
        }, SSO_TIMEOUT_MS);
        ssoConnectCallback.mutate(ssoUrlParams);
        return () => {
          clearTimeout(timeout);
        };
      }
    }
  }, [isInitialRender, ssoUrlParams, ssoConnectCallback]);

  return { ssoLinkState, ssoErrorType };
};
